// Modal "Aniversariantes de hoje" — mockup "Modal Aniversariantes" (600px):
// cabeçalho em gradiente com o ladrilho do bolo, seção da equipe, seção dos
// contatos com os três estados de envio e rodapé com atalho para as
// configurações. Aberto pelo layout quando há aniversariantes no dia.
import React, { useEffect, useState } from "react";

import { useHistory } from "react-router-dom";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import CloseIcon from "@material-ui/icons/Close";
import CakeIcon from "@material-ui/icons/Cake";
import ForumIcon from "@material-ui/icons/Forum";
import SendIcon from "@material-ui/icons/Send";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import SettingsIcon from "@material-ui/icons/Settings";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AEROS, stateText } from "../../styles/aerosTokens";
import {
  initials,
  avatarColor,
  ageLabel,
} from "../../pages/BirthdaySettings/birthdayRules";

// Chave do "Lembrar mais tarde": conveniência por navegador, do dia corrente.
export const snoozeKey = (date = new Date()) =>
  `birthdayModalSnoozed:${date.toISOString().slice(0, 10)}`;

export const isSnoozedToday = () => {
  try {
    return localStorage.getItem(snoozeKey()) === "1";
  } catch (err) {
    // Navegador com armazenamento bloqueado: melhor mostrar o modal do que
    // engolir o aviso por causa de uma preferência que não pôde ser lida.
    return false;
  }
};

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const borderSoft = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  return {
    paper: {
      width: 600,
      maxWidth: "calc(100vw - 32px)",
      backgroundColor: theme.palette.aeros.surface2,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.12)"}`,
      borderRadius: 16,
      overflow: "hidden",
      boxShadow: `0 20px 60px rgba(0,0,0,0.6), 0 0 0 1px ${alpha(
        theme.palette.primary.main,
        0.15
      )}`,
    },
    hero: {
      position: "relative",
      padding: "28px 24px 22px",
      textAlign: "center",
      background: `linear-gradient(135deg, ${alpha(
        theme.palette.primary.main,
        0.35
      )}, ${alpha(AEROS.cyan, 0.25)})`,
      borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      overflow: "hidden",
    },
    heroDots: {
      position: "absolute",
      inset: 0,
      backgroundImage:
        "radial-gradient(circle, rgba(255,255,255,0.12) 1.5px, transparent 1.5px)",
      backgroundSize: "22px 22px",
      opacity: 0.5,
      pointerEvents: "none",
    },
    heroClose: {
      position: "absolute",
      top: 8,
      right: 8,
      color: theme.palette.text.secondary,
      "& .MuiSvgIcon-root": { fontSize: 20 },
    },
    heroTile: {
      position: "relative",
      width: 56,
      height: 56,
      borderRadius: 16,
      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${AEROS.cyan})`,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: `0 10px 30px ${alpha(theme.palette.primary.main, 0.4)}`,
      "& .MuiSvgIcon-root": { fontSize: 30, color: "#07080D" },
    },
    heroTitle: {
      position: "relative",
      fontFamily: AEROS.fontDisplay,
      fontSize: 22,
      fontWeight: 800,
      letterSpacing: "-0.02em",
      marginTop: 12,
      color: theme.palette.text.primary,
    },
    heroSubtitle: {
      position: "relative",
      fontSize: 13,
      color: theme.palette.text.secondary,
      marginTop: 4,
    },
    body: {
      padding: "16px 24px",
      display: "flex",
      flexDirection: "column",
      gap: 12,
      maxHeight: "50vh",
      overflowY: "auto",
      ...theme.scrollbarStylesSoft,
    },
    eyebrow: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
    },
    userRow: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
      borderRadius: 12,
      padding: "12px 14px",
    },
    userAvatarWrap: { position: "relative", flex: "none" },
    userAvatar: {
      width: 44,
      height: 44,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 14,
      fontWeight: 700,
      color: "#07080D",
      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.3)}`,
    },
    userCake: {
      position: "absolute",
      right: -4,
      bottom: -4,
      width: 20,
      height: 20,
      borderRadius: "50%",
      backgroundColor: theme.palette.aeros.surface2,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "& .MuiSvgIcon-root": {
        fontSize: 13,
        color: stateText(theme, AEROS.warning, "warning"),
      },
    },
    userName: {
      fontSize: 14,
      fontWeight: 700,
      color: theme.palette.text.primary,
      display: "flex",
      alignItems: "center",
      gap: 6,
      flexWrap: "wrap",
    },
    profileChip: {
      fontSize: 11,
      fontWeight: 600,
      color: stateText(theme, "#C08BFF", "purple"),
      backgroundColor: alpha(theme.palette.primary.main, 0.18),
      borderRadius: 6,
      padding: "1px 7px",
    },
    userMeta: { fontSize: 12, color: theme.palette.text.secondary },
    contactRow: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderSoft}`,
      borderRadius: 12,
      padding: "11px 14px",
    },
    contactAvatar: {
      width: 40,
      height: 40,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 13,
      fontWeight: 700,
      color: "#07080D",
      flex: "none",
    },
    contactName: {
      fontSize: 13.5,
      fontWeight: 600,
      color: theme.palette.text.primary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    contactMeta: {
      fontSize: 11.5,
      color: theme.palette.text.secondary,
      display: "flex",
      alignItems: "center",
      gap: 6,
      flexWrap: "wrap",
    },
    sentTag: {
      display: "inline-flex",
      alignItems: "center",
      gap: 3,
      color: stateText(theme, AEROS.success, "success"),
      "& .MuiSvgIcon-root": { fontSize: 12 },
    },
    ghostButton: {
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.14)"}`,
      color: theme.palette.text.primary,
      borderRadius: 8,
      padding: "6px 12px",
      fontSize: 12,
      fontWeight: 600,
      textTransform: "none",
      flex: "none",
      "& .MuiButton-startIcon .MuiSvgIcon-root": { fontSize: 15 },
    },
    doneButton: {
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.1)"}`,
      color: theme.palette.aeros.textTertiary,
      borderRadius: 8,
      padding: "6px 12px",
      fontSize: 12,
      fontWeight: 600,
      textTransform: "none",
      flex: "none",
    },
    sendingTag: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      backgroundColor: alpha(AEROS.success, 0.15),
      color: stateText(theme, AEROS.success, "success"),
      borderRadius: 8,
      padding: "6px 12px",
      fontSize: 12,
      fontWeight: 600,
      flex: "none",
      "& .MuiSvgIcon-root": { fontSize: 15 },
    },
    sendButton: {
      backgroundColor: AEROS.success,
      color: "#07080D",
      borderRadius: 8,
      padding: "6px 12px",
      fontSize: 12,
      fontWeight: 700,
      textTransform: "none",
      flex: "none",
      boxShadow: "none",
      "&:hover": { backgroundColor: AEROS.success, opacity: 0.9 },
      "& .MuiButton-startIcon .MuiSvgIcon-root": { fontSize: 15 },
    },
    footer: {
      padding: "14px 24px",
      borderTop: `1px solid ${borderSoft}`,
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexWrap: "wrap",
    },
    footerLink: {
      fontSize: 11.5,
      fontWeight: 500,
      textTransform: "none",
      color: theme.palette.aeros.textTertiary,
      padding: 0,
      minWidth: 0,
      "& .MuiButton-startIcon .MuiSvgIcon-root": { fontSize: 14 },
      "&:hover": { backgroundColor: "transparent", color: theme.palette.text.primary },
    },
    footerActions: { marginLeft: "auto", display: "flex", gap: 10 },
    laterButton: {
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.16)"}`,
      color: theme.palette.text.primary,
      borderRadius: 10,
      padding: "8px 16px",
      fontWeight: 600,
      fontSize: 13,
      textTransform: "none",
    },
    gotItButton: {
      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${AEROS.cyan})`,
      color: "#07080D",
      borderRadius: 10,
      padding: "8px 18px",
      fontWeight: 700,
      fontSize: 13,
      textTransform: "none",
      boxShadow: "none",
    },
    loading: {
      padding: 40,
      display: "flex",
      justifyContent: "center",
    },
    empty: {
      fontSize: 13,
      color: theme.palette.text.secondary,
      textAlign: "center",
      padding: "12px 0",
    },
  };
});

const BirthdayModal = ({ open, onClose }) => {
  const classes = useStyles();
  const history = useHistory();
  const [data, setData] = useState({ users: [], contacts: [] });
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(new Set());
  const [opening, setOpening] = useState(null);

  useEffect(() => {
    if (!open) return;
    async function load() {
      setLoading(true);
      try {
        const resposta = await api.get("/birthdays/today");
        setData(resposta.data?.data || { users: [], contacts: [] });
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [open]);

  const marcaEnviada = (contactId) =>
    setData((atual) => ({
      ...atual,
      contacts: (atual.contacts || []).map((contato) =>
        contato.id === contactId ? { ...contato, messageSent: true } : contato
      ),
    }));

  const handleSend = async (contact) => {
    setSending((atual) => new Set([...atual, contact.id]));
    try {
      await api.post("/birthdays/send-message", { contactId: contact.id });
      marcaEnviada(contact.id);
    } catch (err) {
      // 409 = já enviada hoje. Não é falha: a linha só precisa refletir isso.
      if (err.response?.status === 409) {
        marcaEnviada(contact.id);
      } else {
        toastError(err);
      }
    } finally {
      setSending((atual) => {
        const novo = new Set(atual);
        novo.delete(contact.id);
        return novo;
      });
    }
  };

  // "Parabenizar": reaproveita a conversa direta que já existir com o colega —
  // criar uma nova a cada clique encheria o chat interno de duplicatas.
  const handleGreet = async (user) => {
    setOpening(user.id);
    try {
      const { data: lista } = await api.get("/chats", {
        params: { pageNumber: 1 },
      });
      const existente = (lista?.records || []).find((chat) => {
        const ids = (chat.users || []).map((item) => item.userId || item.user?.id);
        return ids.length === 2 && ids.includes(user.id);
      });
      if (existente) {
        history.push(`/chats/${existente.id}`);
      } else {
        const { data: criado } = await api.post("/chats", {
          users: [{ id: user.id }],
          title: user.name,
        });
        history.push(`/chats/${criado.id}`);
      }
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setOpening(null);
    }
  };

  const handleLater = () => {
    try {
      localStorage.setItem(snoozeKey(), "1");
    } catch (err) {
      // Sem armazenamento a preferência não persiste; fechar já é o esperado.
      toast.info(i18n.t("aeros.birthday.today.later"));
    }
    onClose();
  };

  const users = data.users || [];
  const contacts = data.contacts || [];
  const temAlgo = users.length > 0 || contacts.length > 0;

  if (!open) return null;
  if (!loading && !temAlgo) return null;

  const dataHoje = new Intl.DateTimeFormat(i18n.language || "pt-BR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date());

  const partes = [];
  if (users.length) {
    partes.push(
      i18n.t(
        users.length === 1
          ? "aeros.birthday.today.subtitleTeam"
          : "aeros.birthday.today.subtitleTeamMany",
        { count: users.length }
      )
    );
  }
  if (contacts.length) {
    partes.push(
      i18n.t(
        contacts.length === 1
          ? "aeros.birthday.today.subtitleContacts"
          : "aeros.birthday.today.subtitleContactsMany",
        { count: contacts.length }
      )
    );
  }

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ className: classes.paper }}>
      <div className={classes.hero}>
        <div className={classes.heroDots} />
        <IconButton className={classes.heroClose} onClick={onClose} aria-label="fechar">
          <CloseIcon />
        </IconButton>
        <span className={classes.heroTile}>
          <CakeIcon />
        </span>
        <div className={classes.heroTitle}>{i18n.t("aeros.birthday.today.title")}</div>
        <div className={classes.heroSubtitle}>
          {[dataHoje, partes.join(" · ")].filter(Boolean).join(" · ")}
        </div>
      </div>

      <div className={classes.body}>
        {loading ? (
          <div className={classes.loading}>
            <CircularProgress size={24} />
          </div>
        ) : (
          <>
            {users.length > 0 && (
              <>
                <div className={classes.eyebrow}>
                  {i18n.t("aeros.birthday.today.team")}
                </div>
                {users.map((user) => (
                  <div key={`user-${user.id}`} className={classes.userRow}>
                    <span className={classes.userAvatarWrap}>
                      <span
                        className={classes.userAvatar}
                        style={{ backgroundColor: avatarColor(user.id) }}
                      >
                        {initials(user.name)}
                      </span>
                      <span className={classes.userCake}>
                        <CakeIcon />
                      </span>
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className={classes.userName}>
                        {user.name}
                        {user.profile && (
                          <span className={classes.profileChip}>{user.profile}</span>
                        )}
                      </div>
                      <div className={classes.userMeta}>
                        {i18n.t("aeros.birthday.today.userAge", { count: user.age })}
                        {user.announcementPublished
                          ? ` · ${i18n.t("aeros.birthday.today.announcementPublished")}`
                          : ""}
                      </div>
                    </div>
                    <Button
                      className={classes.ghostButton}
                      startIcon={
                        opening === user.id ? undefined : <ForumIcon />
                      }
                      onClick={() => handleGreet(user)}
                      disabled={opening === user.id}
                    >
                      {opening === user.id ? (
                        <CircularProgress size={15} />
                      ) : (
                        i18n.t("aeros.birthday.today.greet")
                      )}
                    </Button>
                  </div>
                ))}
              </>
            )}

            {contacts.length > 0 && (
              <>
                <div className={classes.eyebrow} style={{ marginTop: 4 }}>
                  {i18n.t("aeros.birthday.today.contacts")}
                </div>
                {contacts.map((contact) => {
                  const enviando = sending.has(contact.id);
                  return (
                    <div key={`contact-${contact.id}`} className={classes.contactRow}>
                      <span
                        className={classes.contactAvatar}
                        style={{ backgroundColor: avatarColor(contact.id) }}
                      >
                        {initials(contact.name)}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className={classes.contactName} title={contact.name}>
                          {contact.name}
                        </div>
                        <div className={classes.contactMeta}>
                          {[contact.contactNumber, ageLabel(contact.age)]
                            .filter(Boolean)
                            .join(" · ")}
                          {contact.messageSent && (
                            <span className={classes.sentTag}>
                              <DoneAllIcon />
                              {i18n.t("aeros.birthday.today.sentAt", {
                                time: contact.sentAt || "",
                              }).trim()}
                            </span>
                          )}
                        </div>
                      </div>
                      {contact.messageSent ? (
                        <span className={classes.doneButton}>
                          {i18n.t("aeros.birthday.today.alreadySent")}
                        </span>
                      ) : enviando ? (
                        <span className={classes.sendingTag}>
                          <HourglassEmptyIcon />
                          {i18n.t("aeros.birthday.today.sending")}
                        </span>
                      ) : (
                        <Button
                          className={classes.sendButton}
                          startIcon={<SendIcon />}
                          onClick={() => handleSend(contact)}
                        >
                          {i18n.t("aeros.birthday.today.send")}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </>
            )}

            {!users.length && !contacts.length && (
              <div className={classes.empty}>
                {i18n.t("aeros.birthday.today.empty")}
              </div>
            )}
          </>
        )}
      </div>

      <div className={classes.footer}>
        <Button
          className={classes.footerLink}
          startIcon={<SettingsIcon />}
          onClick={() => {
            history.push("/birthday-settings");
            onClose();
          }}
        >
          {i18n.t("aeros.birthday.today.configure")}
        </Button>
        <div className={classes.footerActions}>
          <Button className={classes.laterButton} onClick={handleLater}>
            {i18n.t("aeros.birthday.today.later")}
          </Button>
          <Button className={classes.gotItButton} onClick={onClose}>
            {i18n.t("aeros.birthday.today.gotIt")}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default BirthdayModal;
